import React, { createContext, useEffect, useState, useCallback, useRef } from 'react';
import keycloak from './keycloak';
import { AuthUser, OrgRole, Organization } from '@core/types';
import { orgService } from '@core/supabase/services/orgService';

const ACTIVE_ORG_LS_KEY = 'ggj.activeOrgId';

export interface OrgOption {
  org: Organization;
  role: OrgRole;
  orgUserId: string;
}

export interface AuthContextValue {
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** All workspaces the signed-in user belongs to. */
  allOrgs: OrgOption[];
  /** Keycloak login. Pass a path (e.g. `/dashboard`) to control where the user returns after auth. */
  login: (redirectPath?: string) => void;
  logout: () => void;
  refreshToken: () => Promise<boolean>;
  /** Re-fetches the user's org from Supabase and updates context state. */
  refreshUser: () => Promise<void>;
  /** Switch to a different workspace without re-logging in. */
  switchOrg: (orgId: string) => void;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * Module-level flag — survives React 18 StrictMode double-invoke.
 * Keycloak's JS adapter throws if .init() is called more than once on the
 * same instance.
 */
let keycloakInitialized = false;

/** Preserve deep links: Keycloak must return the user to the same path (not origin-only → `/` → dashboard). */
function appRedirectUri(): string {
  if (typeof window === 'undefined') return '';
  return `${window.location.origin}${window.location.pathname}${window.location.search}`;
}

const B2B_ROLES: OrgRole[] = [
  'owner', 'admin', 'senior_recruiter', 'junior_recruiter', 'viewer',
];

/** Extract base user info from the Keycloak token (no org data). */
function extractBaseUser(): Omit<AuthUser, 'orgId' | 'orgName' | 'orgUserId'> | null {
  if (!keycloak.authenticated || !keycloak.tokenParsed) return null;
  const p = keycloak.tokenParsed as Record<string, unknown>;
  const realmRoles =
    (p['realm_access'] as { roles?: string[] } | undefined)?.roles ?? [];
  return {
    id:    (p['sub'] as string) ?? '',
    email: (p['email'] as string) ?? '',
    name:  (p['name'] as string) ?? (p['preferred_username'] as string) ?? '',
    roles: realmRoles.filter((r) => B2B_ROLES.includes(r as OrgRole)) as OrgRole[],
  };
}

/**
 * §10.3 — Identity + tenancy
 *
 * - **Keycloak:** Browser session via `keycloak-js` (PKCE). JWT claims supply `sub`, `email`, and optional
 *   realm roles; see `extractBaseUser`.
 * - **Org binding:** After login, `orgService.getOrgByKeycloakSub(sub)` loads the row in `ggj_b2b_org_users`
 *   and organisation. That row’s role becomes the **authoritative** RBAC source for `usePermissions`
 *   (unless no org row exists — then Keycloak realm roles are a fallback only).
 * - **API calls:** The Supabase JS client uses the anon or service key from env (see `client.ts`). Production
 *   flows should still enforce **org_id** in Edge/RPC/RLS; the UI never trusts client-side checks alone.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser]   = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [allOrgs, setAllOrgs] = useState<OrgOption[]>([]);
  const initRef = useRef(false);

  /** Look up ALL orgs the user belongs to, then activate the persisted one (or oldest). */
  const hydrateOrgFromSupabase = useCallback(async (base: Omit<AuthUser, 'orgId' | 'orgName' | 'orgUserId'>) => {
    try {
      const orgs = await orgService.listUserOrgs(base.id);
      setAllOrgs(orgs);

      // Prefer a previously-chosen org (stored in localStorage); fall back to oldest.
      const savedId = (() => {
        try { return window.localStorage.getItem(ACTIVE_ORG_LS_KEY); } catch { return null; }
      })();
      const match = savedId ? orgs.find((o) => o.org.id === savedId) : null;
      const result = match ?? orgs[0] ?? null;

      setUser({
        ...base,
        orgId:     result?.org.id     ?? null,
        orgName:   result?.org.name   ?? null,
        orgUserId: result?.orgUserId  ?? null,
        roles:     result ? [result.role] : base.roles,
        credits:   result?.org?.credits_balance ?? 0,
      });
    } catch {
      setUser({ ...base, orgId: null, orgName: null, orgUserId: null, credits: 0 });
    }
  }, []);

  const refreshUser = useCallback(async () => {
    const base = extractBaseUser();
    if (!base) return;
    await hydrateOrgFromSupabase(base);
  }, [hydrateOrgFromSupabase]);

  /** Switch active workspace — no re-login needed. Persisted so refresh keeps it. */
  const switchOrg = useCallback((orgId: string) => {
    const match = allOrgs.find((o) => o.org.id === orgId);
    if (!match) return;
    try { window.localStorage.setItem(ACTIVE_ORG_LS_KEY, orgId); } catch { /* */ }
    setUser((prev) =>
      prev
        ? {
            ...prev,
            orgId:     match.org.id,
            orgName:   match.org.name,
            orgUserId: match.orgUserId,
            roles:     [match.role],
            credits:   match.org.credits_balance ?? 0,
          }
        : prev,
    );
  }, [allOrgs]);

  useEffect(() => {
    if (initRef.current || keycloakInitialized) {
      if (keycloakInitialized && keycloak.authenticated) {
        setToken(keycloak.token ?? null);
        const base = extractBaseUser();
        if (base) {
          hydrateOrgFromSupabase(base).finally(() => setIsLoading(false));
        } else {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
      return;
    }

    initRef.current     = true;
    keycloakInitialized = true;

    keycloak
      .init({
        checkLoginIframe: false,
        pkceMethod: 'S256',
      })
      .then(async (authenticated) => {
        if (authenticated) {
          setToken(keycloak.token ?? null);
          const base = extractBaseUser();
          if (base) {
            await hydrateOrgFromSupabase(base);
          }
        }
      })
      .catch((err) => {
        console.warn('[AuthProvider] Keycloak init failed:', err);
      })
      .finally(() => {
        setIsLoading(false);
      });

    keycloak.onTokenExpired = () => {
      keycloak.updateToken(60).then((refreshed) => {
        if (refreshed) {
          setToken(keycloak.token ?? null);
          refreshUser();
        }
      });
    };
  }, [hydrateOrgFromSupabase, refreshUser]);

  const login = useCallback((redirectPath?: string) => {
    if (typeof window === 'undefined') return;
    const uri =
      redirectPath != null && String(redirectPath).trim() !== ''
        ? `${window.location.origin}${redirectPath.startsWith('/') ? redirectPath : `/${redirectPath}`}`
        : appRedirectUri();
    keycloak.login({ redirectUri: uri });
  }, []);

  const logout = useCallback(() => {
    const homeUri = typeof window !== 'undefined' ? window.location.origin + '/' : '/';
    keycloak.logout({ redirectUri: homeUri });
  }, []);

  const refreshToken = useCallback(async (): Promise<boolean> => {
    try {
      const refreshed = await keycloak.updateToken(30);
      if (refreshed) {
        setToken(keycloak.token ?? null);
        await refreshUser();
      }
      return true;
    } catch {
      return false;
    }
  }, [refreshUser]);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated: keycloak.authenticated ?? false,
        isLoading,
        allOrgs,
        login,
        logout,
        refreshToken,
        refreshUser,
        switchOrg,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
